import React, { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'pdfjs-dist/build/pdf.worker.entry';

import { DocumentIcon } from '../ui/icons';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.js',
    import.meta.url
).toString();

// Renders the first page of a bank statement PDF as a compact thumbnail,
// lazily: the blob is only fetched (via loadPdf, which is cached by the parent)
// once the tile scrolls into view, so applicants with many statements don't
// fetch everything on mount. The page is rendered at the tile's width and the
// box clips it to a fixed height, matching the cropped document tiles.
const BankStatementThumb = ({ uri, loadPdf, heightClass = 'h-24' }) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    const [url, setUrl] = useState(null);
    const [width, setWidth] = useState(0);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!ref.current) return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setInView(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '200px' }
        );
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!ref.current) return undefined;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect?.width;
            if (w) setWidth(Math.round(w));
        });
        ro.observe(ref.current);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        let active = true;
        if (inView && !url && !failed) {
            loadPdf(uri)
                .then((res) => {
                    if (active && res) setUrl(res.url);
                    else if (active) setFailed(true);
                })
                .catch(() => active && setFailed(true));
        }
        return () => {
            active = false;
        };
    }, [inView, url, failed, uri, loadPdf]);

    return (
        <div
            ref={ref}
            className={`relative ${heightClass} w-full overflow-hidden bg-[#F5F6FA] flex items-center justify-center`}
        >
            {failed ? (
                <div className="flex flex-col items-center gap-1 text-[#5B3FE0]">
                    <DocumentIcon className="h-7 w-7" />
                    <span className="text-[10px] font-bold uppercase tracking-wide text-[#6B7280]">PDF</span>
                </div>
            ) : url && width ? (
                <div className="absolute inset-x-0 top-0">
                    <Document file={url} loading="" error="" onLoadError={() => setFailed(true)}>
                        <Page
                            pageNumber={1}
                            width={width}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            loading=""
                            error=""
                        />
                    </Document>
                </div>
            ) : (
                <span className="inline-flex h-7 w-7 animate-pulse rounded-md bg-[#E5E1FA]" />
            )}
        </div>
    );
};

export default BankStatementThumb;
